import styled from "@emotion/styled";
import { ExpandContent, H3, Link, Text } from "atoms";
import { Thumbnail } from "features/images";
import type { Artist } from "../types";

type ArtistCollectionsProps = {
  collections: Artist["artistCollections"];
};

function ArtistCollections({ collections, ...props }: ArtistCollectionsProps) {
  return (
    <CollectionsList {...props}>
      {collections.map((collection) => (
        <ArtistCollection key={collection.collectionName} {...collection} />
      ))}
    </CollectionsList>
  );
}

type ArtistCollectionProps = Artist["artistCollections"][number];

function ArtistCollection({
  collectionLinks,
  collectionName,
  collectionDescription = "",
  collectionImage,
}: ArtistCollectionProps) {
  const isSingleLink = collectionLinks.length === 1;

  return (
    <CollectionsListItem>
      <Thumbnail rounded src={collectionImage} alt={collectionName} />

      <H3 size="xs">{collectionName}</H3>

      <Description>
        {collectionDescription.length >= 200 ? (
          <ExpandContent
            showLabel="Expand description"
            hideLabel="Collapse description"
          >
            <Text size="xs">{collectionDescription}</Text>
          </ExpandContent>
        ) : (
          <Text size="xs">{collectionDescription}</Text>
        )}

        {isSingleLink ? (
          <Text size="xs">
            <Link href={collectionLinks[0].url}>
              View on {collectionLinks[0].type[0].toUpperCase()}
              {collectionLinks[0].type.slice(1)}
            </Link>
          </Text>
        ) : (
          <LinkList>
            {collectionLinks.map(({ type, url }) => (
              <li key={url}>
                <Link href={url}>
                  {type[0].toUpperCase()}
                  {type.slice(1)}
                </Link>
              </li>
            ))}
          </LinkList>
        )}
      </Description>
    </CollectionsListItem>
  );
}

const CollectionsList = styled.ul({
  display: "grid",
  gap: "1rem",
  listStyleType: "none",
  margin: 0,
  padding: 0,
  "li::before": {
    content: '"\\200B"',
    position: "absolute",
    height: 0,
  },
  "@media (min-width: 440px)": {
    gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))",
  },
  "@media (min-width: 831px)": {
    gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
  },
});

const CollectionsListItem = styled.li(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "75px 1fr",
  gridTemplateRows: "auto 1fr",
  gridTemplateAreas: `
    "img title"
    "desc desc"
  `,
  alignItems: "center",
  borderRadius: 10,
  boxShadow: theme.boxShadow.light,
  gap: 10,
  padding: "1rem",
  img: {
    gridArea: "img",
    width: 75,
    height: 75,
    objectFit: "cover",
  },
  h3: {
    margin: "0",
    gridArea: "title",
  },
  a: {
    display: "inline-block",
    fontWeight: "bold",
    marginTop: 5,
  },
  "@media (min-width: 440px)": {
    alignItems: "start",
    gap: "10px 1em",
    gridTemplateAreas: `
      "img title"
      "img desc"
      `,
  },
}));

const Description = styled.div(({ theme }) => ({
  gridArea: "desc",
  p: {
    fontSize: theme.fontSizes.xxs,
    lineHeight: "1.4",
    marginBottom: 0,
  },
}));

const LinkList = styled.ul(({ theme }) => ({
  listStyleType: "disc",
  paddingLeft: "0.75rem",
  li: {
    fontSize: theme.fontSizes.xxs,
  },
}));

export { ArtistCollections };
